import React, { useState, useEffect } from 'react'
import { Flashcard, Quality } from '../types'
import './FlashcardComponent.css'

interface FlashcardComponentProps {
  card: Flashcard
  onReview?: (cardId: string, quality: Quality) => void
  showAnswer?: boolean
  isFlipped?: boolean
  onFlip?: () => void
}

const FlashcardComponent: React.FC<FlashcardComponentProps> = ({
  card,
  onReview,
  isFlipped = false,
  onFlip
}) => {
  const [isFlipping, setIsFlipping] = useState(false)
  const [localIsFlipped, setLocalIsFlipped] = useState(isFlipped)

  useEffect(() => {
    setLocalIsFlipped(isFlipped)
  }, [isFlipped])

  const handleFlip = () => {
    if (onFlip) {
      onFlip()
    } else {
      setIsFlipping(true)
      setTimeout(() => {
        setLocalIsFlipped(!localIsFlipped)
        setIsFlipping(false)
      }, 300)
    }
  }

  const handleReview = (quality: Quality) => {
    if (onReview) {
      onReview(card.card_id, quality)
    }
  }

  const getDifficultyColor = (difficulty?: string) => {
    switch (difficulty) {
      case 'N5': return '#4CAF50'
      case 'N4': return '#8BC34A'
      case 'N3': return '#FFC107'
      case 'N2': return '#FF9800'
      case 'N1': return '#F44336'
      default: return '#9E9E9E'
    }
  }

  const getCardTypeIcon = (cardType?: string) => {
    switch (cardType) {
      case 'vocabulary': return '📖'
      case 'kanji': return '漢'
      case 'grammar': return '✏️'
      default: return '📝'
    }
  }

  return (
    <div className={`flashcard-container ${isFlipping ? 'flipping' : ''}`}>
      <div className={`flashcard ${localIsFlipped ? 'flipped' : ''}`}>
        {/* Front of the card */}
        <div className="flashcard-face flashcard-front">
          <div className="flashcard-header">
            {card.difficulty && (
              <div className="difficulty-badge" style={{ backgroundColor: getDifficultyColor(card.difficulty) }}>
                {card.difficulty}
              </div>
            )}
            <div className="card-type">{getCardTypeIcon(card.card_type)}</div>
          </div>

          <div className="flashcard-content">
            <div className="front-text">
              {card.front_text}
            </div>
            {card.reading && (
              <div className="reading">
                {card.reading}
              </div>
            )}
            {card.tags.length > 0 && (
              <div className="tags">
                {card.tags.map(tag => (
                  <span key={tag} className="tag">{tag}</span>
                ))}
              </div>
            )}
          </div>

          <div className="flashcard-actions">
            <button
              className="flip-btn"
              onClick={handleFlip}
              aria-label="カードを裏返す"
            >
              {localIsFlipped ? '答えを隠す' : '答えを見る'}
            </button>
          </div>
        </div>

        {/* Back of the card */}
        <div className="flashcard-face flashcard-back">
          <div className="flashcard-header">
            {card.difficulty && (
              <div className="difficulty-badge" style={{ backgroundColor: getDifficultyColor(card.difficulty) }}>
                {card.difficulty}
              </div>
            )}
            <div className="card-type">{getCardTypeIcon(card.card_type)}</div>
          </div>

          <div className="flashcard-content">
            <div className="back-text">
              {card.back_text}
            </div>
            {card.meaning && (
              <div className="meaning">
                {card.meaning}
              </div>
            )}
            {card.examples && card.examples.length > 0 && (
              <div className="example">
                <strong>例文:</strong>
                <p>{card.examples[0]}</p>
              </div>
            )}
          </div>

          {onReview && (
            <div className="review-actions">
              <div className="review-question">このカードはどうでしたか？</div>
              <div className="quality-buttons">
                <button
                  className="quality-btn again"
                  onClick={() => handleReview(Quality.AGAIN)}
                  title="もう一度 - 思い出せなかった"
                >
                  もう一度
                </button>
                <button
                  className="quality-btn hard"
                  onClick={() => handleReview(Quality.HARD)}
                  title="難しい - 思い出すのに苦労した"
                >
                  難しい
                </button>
                <button
                  className="quality-btn good"
                  onClick={() => handleReview(Quality.GOOD)}
                  title="良い - 正しく思い出せた"
                >
                  良い
                </button>
                <button
                  className="quality-btn easy"
                  onClick={() => handleReview(Quality.EASY)}
                  title="簡単 - 簡単に思い出せた"
                >
                  簡単
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FlashcardComponent